import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { isMasterEmail } from '../utils/masterAccess';

const AuthContext = createContext(undefined);

// Where the "reset your password" mail sends the user back to: this same site, which
// then shows ResetPassword (see isPasswordRecovery below).
const PASSWORD_RESET_URL = `${window.location.origin}/`;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  // True while the user arrived via a "reset password" email link — they
  // have a valid session but need to set a new password before using the app.
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      if (event === 'PASSWORD_RECOVERY') {
        setIsPasswordRecovery(true);
      }
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  const signUp = async (email, password) => {
    const { data, error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
    // For an email that already has an account, Supabase does not answer with an error
    // (so nobody can probe which addresses exist): it answers with a look-alike user that
    // has no identities, and sends no mail. That is the only sign that the account exists.
    if (data?.user && Array.isArray(data.user.identities) && data.user.identities.length === 0) {
      const exists = new Error('User already registered');
      exists.code = 'user_already_exists';
      throw exists;
    }
  };

  // Sends the "reset your password" mail.
  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: PASSWORD_RESET_URL });
    if (error) throw error;
  };

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const user = session?.user ?? null;
  const isMasterAccess = isMasterEmail(user?.email);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      loading,
      signUp,
      resetPassword,
      signIn,
      signOut,
      updatePassword,
      isMasterAccess,
      isPasswordRecovery,
      clearPasswordRecovery,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
